import { useState, useEffect, useMemo } from "react";
import { TransformedProduct } from "../types/productApiTypes";
import { useGetProductVariationsQuery } from "../api/productApi";

export const useVariationSelection = (
  product: TransformedProduct | null | undefined,
  initialAttributes?: { [key: string]: string } // optional parameter
) => {
  // --- State Management ---
  // Initialize state from the optional prop, falling back to an empty object
  const [selectedAttributes, setSelectedAttributes] = useState<{
    [key: string]: string;
  }>(initialAttributes || {});

  // --- Data Fetching ---
  // We get both isLoading and isFetching to provide granular control to consuming components.
  const {
    data: variations,
    isLoading: isVariationsLoading,
    isFetching: isVariationsFetching,
  } = useGetProductVariationsQuery(product?.id as number, {
    skip: !product?.id || !product.isVariable,
  });

  // --- Effect to set initial default attributes ---
  // This effect runs when the parent product data is available.
  useEffect(() => {
    // If initial attributes were provided from the outside (e.g., from the URL),
    // they are already set in the state initializer. The main purpose of this effect
    // is to set the default attributes if no initial selection was made externally.
    if (initialAttributes && Object.keys(initialAttributes).length > 0) {
      // If the passed initialAttributes change, we should update our state to reflect that.
      setSelectedAttributes(initialAttributes);
      return;
    }

    // This part of the logic only runs if `initialAttributes` is empty or undefined.
    if (product?.isVariable && product.defaultVariationData) {
      const defaultAttrs =
        product.defaultVariationData.attributes.reduce(
          (acc, attr) => {
            if (attr.taxonomy) {
              acc[attr.taxonomy] = attr.term.slug;
            }
            return acc;
          },
          {} as { [key: string]: string }
        ) || {};
      setSelectedAttributes(defaultAttrs);
    } else {
      // For simple products or variable products without a default, ensure state is clean.
      setSelectedAttributes({});
    }
  }, [product, initialAttributes]); // The hook re-runs if either the product or the initial attributes change.

  // --- Derived State: Find the matching variation ---
  // We use useMemo here instead of useEffect/useState to calculate the variation synchronously.
  // This prevents the "flash of error" where attributes are selected but the variation
  // hasn't been set in state yet during the first render cycle.
  const selectedVariation = useMemo(() => {
    if (!product?.isVariable || !variations || variations.length === 0) {
      return null;
    }

    const requiredAttributeCount =
      product.enrichedAttributes.filter((a) => a.hasVariations).length || 0;
    const selectedAttributeCount = Object.keys(selectedAttributes).length;

    if (selectedAttributeCount < requiredAttributeCount) {
      return null;
    }

    const matchedVariation = variations.find((variation) => {
      const mapping = product.variationAttributeMappings.find(
        (v) => v.id === variation.id
      );
      if (!mapping) return false;

      // This is the crucial logic to correctly handle variations with "Any" attributes.
      // A variation is considered an exact match ONLY IF:
      // 1. The number of attributes the user has selected (`selectedAttributeCount`) is IDENTICAL
      //    to the number of attributes specifically defined for this variation in its mapping (`mapping.attributes.length`).
      // 2. EVERY attribute in this variation's mapping has a value that matches the user's selection.
      //
      // WHY THIS WORKS FOR "ANY" ATTRIBUTES:
      // A variation for "Color: Red" and "Size: Any" will only have 'pa_color' in its mapping,
      // because 'pa_size' is not specific. Its `mapping.attributes.length` will be 1.
      // If a user selects "Color: Red" AND "Size: Large", their `selectedAttributeCount` is 2.
      // The check `1 === 2` will fail, correctly rejecting the partial "Any Size" match. This prevents
      // a situation where the user's 'Large' selection is ignored. A match only occurs if a
      // concrete variation for "Red" and "Large" actually exists.
      return (
        mapping.attributes.length === selectedAttributeCount &&
        mapping.attributes.every(
          (attr) => selectedAttributes[attr.taxonomy] === attr.value
        )
      );
    });

    return matchedVariation || null;
  }, [selectedAttributes, variations, product]);

  // --- Event Handler ---
  const handleAttributeSelect = (
    attributeTaxonomy: string,
    termSlug: string
  ) => {
    setSelectedAttributes((prev) => ({
      ...prev,
      [attributeTaxonomy]: termSlug,
    }));
  };

  return {
    selectedAttributes,
    selectedVariation,
    handleAttributeSelect,
    // For non-variable products, the following properties are always false as the query is skipped.
    isSelectionLoading: isVariationsLoading, // Expose the initial loading state with a clearer name
    isSelectionFetching: isVariationsFetching, // Expose the refetching state with a clearer name
  };
};
